'use client';

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/lib/store/settings';
import { createLogger } from '@/lib/logger';

const log = createLogger('useConfigVersionPolling');

/**
 * Periodically re-syncs admin defaults so that changes the admin makes in
 * the backend (provider configs, role voices, TTS voices, etc.) take effect
 * in the running client without a manual page reload.
 *
 * 老板 P0 2026-09-04：把默认轮询间隔从 5 秒恢复回 1 秒。
 *  - version 在 `Config.php::edit()/del()` 和 `Role.php::save()` 通过 `KeyManager::bumpConfigVersion()` 立刻 +1
 *  - 因此 1 秒轮询可保证「点保存立即生效」（=next-tick fetchAdminDefaults）
 *  - 客户端 in-flight 单例去重（settings.ts 的 inFlightAdminDefaultsPromise）+ force=true 绕过 1.5 秒成功 throttle
 *  - nginx /dongniai/api/config/defaults 加了精确 location = proxy_cache off（见 deploy/nginx），
 *    不会再被 nginx 30 秒缓存截胡
 *
 * Two-tier polling strategy (per product spec — "点保存立即生效"):
 *   1. Every 1s, fetch `?method=configVersion` from dongniai_public.php
 *      → returns ~50 bytes (just `{ver: 1735000000}`).
 *   2. Only when the version changes, call `fetchAdminDefaults(true)` (force=true
 *      bypass the 1.5s success throttle, even if mount-time fetch was <1.5s ago)
 *      → pulls the full defaultConfig ≈ 30 KB.
 *
 * 取消旧版 5 秒节流的 FULL_FETCH_THROTTLE_MS：
 *  - 多次 home/generation-preview/classroom 各自挂 hook，version 变化时都触发 force 调用
 *  - 模块级 inFlightAdminDefaultsPromise 自动去重（同一时刻共享同一个 Promise）
 *  - 因此不再需要每页一份的 5 秒全量节流
 *
 * The version itself is bumped by Config.php::edit()/del() and Role.php::save()
 * via KeyManager::bumpConfigVersion() (single Redis SET, O(1)).
 *
 * Use in any page that depends on admin-driven defaults:
 *   useConfigVersionPolling();
 */
export function useConfigVersionPolling(intervalMs: number = 1000): void {
  const lastVersionRef = useRef<number>(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  // 【v24 P0 FIX 2026-07-11】指数退避，移植 v13 commit 5c4d66f4 模式（classroom auto-retry 2/4/8/16/32s）
  // 真因：老板浏览器看到 /api/config/defaults 连发 10 次 504
  //       旧逻辑: `if (!res.ok) return;` + polling 1s 一次 → 504 后立刻 1s 再打 → 死循环打
  // 修法：连续错误 → 退避 5s/10s/20s/40s/80s → 8 次仍失败 → 停掉 polling 让用户手动刷新
  //   （配套 settings.ts:1541 把 silent return 改成 throw，让 catch 块能感知错误）
  // 验证：use-classroom-timer.ts 实战 7+ 次，retry 不再死循环白屏
  const configDefaultsErrorsRef = useRef(0);
  const lastConfigDefaultsErrorAtRef = useRef(0);
  const CONFIG_DEFAULTS_MAX_ERRORS = 8;
  const CONFIG_DEFAULTS_BACKOFF_BASE_MS = 5000;
  const CONFIG_DEFAULTS_BACKOFF_CAP_MS = 80000;
  const configDefaultsStoppedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sync = async () => {
      try {
        const res = await fetch('/dongniai_public.php?method=configVersion', {
          cache: 'no-store',
          credentials: 'include',
        });
        if (!res.ok) return;
        const json = (await res.json()) as { success?: boolean; data?: { version?: number } };
        const ver = json?.data?.version;
        if (typeof ver !== 'number') return;

        if (lastVersionRef.current === -1) {
          // First poll: just record the version, don't trigger a fetch.
          // The store is already populated by the page's own mount-time
          // fetchAdminDefaults() call; re-fetching here would be a no-op
          // double round trip.
          lastVersionRef.current = ver;
          return;
        }
        if (ver !== lastVersionRef.current) {
          lastVersionRef.current = ver;
          // 老板 P0 2026-09-04：版本变化 → 立即 fetch full config
          // 用 force=true 跳 settings.ts 的 1500ms 成功 throttle，保证 ≤ 1 秒拿到新配置
          // 模块级 inFlight 仍会复用同一次请求，多 hook（home/generation/classroom）不会雪崩
          // 【v24 P0 FIX 2026-07-11】指数退避检查（仍然保留，失败时不让 polling 打死后端）
          if (configDefaultsStoppedRef.current) {
            return;
          }
          if (configDefaultsErrorsRef.current > 0) {
            const errCount = configDefaultsErrorsRef.current;
            if (errCount >= CONFIG_DEFAULTS_MAX_ERRORS) {
              log.error(
                `[useConfigVersionPolling] configDefaults reached ${CONFIG_DEFAULTS_MAX_ERRORS} consecutive errors, ` +
                `stopping version sync. User must reload page to recover.`
              );
              configDefaultsStoppedRef.current = true;
              return;
            }
            const backoffMs = Math.min(
              CONFIG_DEFAULTS_BACKOFF_CAP_MS,
              CONFIG_DEFAULTS_BACKOFF_BASE_MS * Math.pow(2, errCount - 1)
            );
            const sinceLastErr = Date.now() - lastConfigDefaultsErrorAtRef.current;
            if (sinceLastErr < backoffMs) {
              // 仍在退避窗口，跳过 fetchAdminDefaults（version 记录保留）
              return;
            }
          }

          try {
            await useSettingsStore.getState().fetchAdminDefaults(true);
            configDefaultsErrorsRef.current = 0; // 成功 → 重置退避计数
          } catch (e) {
            configDefaultsErrorsRef.current += 1;
            lastConfigDefaultsErrorAtRef.current = Date.now();
            log.warn(
              `[useConfigVersionPolling] configDefaults fetch failed (${configDefaultsErrorsRef.current}/${CONFIG_DEFAULTS_MAX_ERRORS}): ${String(e)}`
            );
          }
        }
      } catch {
        // Network blip / parse error / etc. — silently retry next tick.
      }
    };

    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (document.visibilityState === 'visible') {
          void sync();
        }
      }, intervalMs);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User came back to the tab — fire one immediate check so a change
        // they missed while away is reflected within ~1 round trip rather
        // than waiting up to `intervalMs`.
        void sync();
      }
    };
    visibilityHandlerRef.current = onVisibilityChange;

    startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopInterval();
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
        visibilityHandlerRef.current = null;
      }
    };
  }, [intervalMs]);
}
